import { FormEvent, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PASSWORD_MIN_LENGTH,
  Permission,
  UserStatus,
  assignableRoles,
  canHoldRole,
  canManageRole,
  userFitsSite,
} from '@construction/shared';
import { rolesApi, sitesApi, usersApi, type Role, type RoleSummary, type User } from '@/api/endpoints';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  FilterBar,
  Input,
  ListGroup,
  ListRow,
  PageHeader,
  Select,
  Spinner,
  TableRow,
  Tag,
} from '@/components/ui';
import { CheckList } from '@/components/CheckList';
import { OrgFilter, OrgSelect, type OrgChoice } from '@/features/orgs';
import { StatusTabs } from '@/features/filters/StatusTabs';
import { formatMoney } from '@/lib/format';
import { usePermissions } from '@/lib/permissions';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useAuthStore } from '@/store/auth';

/**
 * 能挂在「归这家的人」身上的角色（CR-073）：自己管得了的（canManageRole）∩ 这家的人能挂的（canHoldRole）。
 * `target === undefined` 是平台账号还没选归哪家：先只列共享的 —— 选了哪家都挂得上的那些。
 */
function rolesFor(myRole: RoleSummary, roles: Role[], target: number | null | undefined): Role[] {
  return assignableRoles(myRole, roles).filter((r) =>
    target === undefined ? r.orgId === null : canHoldRole(r, target),
  );
}

export function UsersPage() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const { can, isPlatform } = usePermissions();
  // 有 user.manage 才能建账号（CR-038）。具体能动谁由每一行自己判断（canManageRole）。
  const canCreate = can(Permission.USER_MANAGE);
  const toMessage = useErrorMessage();

  // 输入框的值和真正发出去的查询分开：点「搜索」才把 draft 提交成 search。
  // 边打边搜在几十条数据上没问题，但工号是一串数字，输到一半的查询没有意义，
  // 每敲一个字发一次请求也白费。
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [creating, setCreating] = useState(false);
  // 正在编辑谁（CR-078）。抽屉挂在页面上、人从列表里取 —— 停用、改名之后列表刷新，抽屉里跟着变
  const [editingId, setEditingId] = useState<number | null>(null);
  // 平台账号按单位筛（CR-073）：'' = 全部，'platform' = 平台账号，数字 = 那一家
  const [org, setOrg] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['users', search, page, pageSize, org],
    queryFn: () => usersApi.list({ search: search || undefined, page, pageSize, org: org || undefined }),
  });

  // 角色列表给建账号 / 改角色的下拉用。只在有用户管理权限时拉 —— 只看列表的人不需要
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
    enabled: canCreate,
  });

  const submitSearch = () => {
    setSearch(draft.trim());
    // 换了搜索词就回到第一页，否则可能停在一个已经不存在的页码上
    setPage(1);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  // 挪到别的公司、筛选之后不在这一页了，抽屉跟着关
  const editingUser = editingId === null ? null : (data?.items.find((u) => u.id === editingId) ?? null);

  return (
    <>
      <PageHeader
        title={t('user.title')}
        description={t('user.subtitle')}
        action={
          canCreate ? (
            <Button onClick={() => setCreating(true)} data-action="user.create">
              {t('user.create')}
            </Button>
          ) : undefined
        }
      />

      <Drawer open={creating} title={t('user.create')} onClose={() => setCreating(false)}>
        <CreateUserForm roles={roles.data ?? []} onDone={() => setCreating(false)} />
      </Drawer>
      <Drawer
        open={editingUser !== null}
        size="wide"
        title={editingUser ? t('user.editTitle', { name: editingUser.name }) : ''}
        subtitle={
          editingUser && (
            <span className="tabular-nums">
              {t('user.userId')} {editingUser.id} · {t('user.employeeNo')} {editingUser.employeeNo} · {editingUser.role.name}
            </span>
          )
        }
        onClose={() => setEditingId(null)}
      >
        {editingUser && me && (
          <UserEditPanel
            key={editingUser.id}
            user={editingUser}
            myId={me.id}
            myRole={me.role}
            roles={roles.data ?? []}
            onClose={() => setEditingId(null)}
          />
        )}
      </Drawer>

      {/* 工具条（CR-082）：搜索框（回车搜）+ 公司胶囊，右边一共多少个账号 */}
      <FilterBar summary={data && data.total > 0 && t('user.totalCount', { count: data.total })}>
        <form
          className="flex min-w-56 max-w-sm flex-1 gap-2 max-md:max-w-none max-md:basis-full"
          onSubmit={(e) => {
            e.preventDefault();
            submitSearch();
          }}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('user.searchPlaceholder')}
            type="search"
            aria-label={t('app.search')}
          />
          <Button type="submit" variant="secondary" className="shrink-0">
            {t('app.search')}
          </Button>
          {search && (
            <Button
              type="button"
              variant="ghost"
              className="shrink-0"
              onClick={() => {
                setDraft('');
                setSearch('');
                setPage(1);
              }}
            >
              {t('app.clear')}
            </Button>
          )}
        </form>

        {isPlatform && (
          <OrgFilter
            value={org}
            onChange={(next) => {
              setOrg(next);
              setPage(1);
            }}
            platformOption={t('user.platformAccounts')}
          />
        )}
      </FilterBar>

      {isLoading && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}
      {isError && <Alert>{toMessage(error)}</Alert>}

      {data && data.items.length === 0 && <EmptyState title={t('user.empty')} />}

      {data && data.items.length > 0 && (
        <>
          {/* 窄屏、App：列表 */}
          <ListGroup className="xl:hidden">
            {data.items.map((user) => (
              <UserListRow
                key={user.id}
                user={user}
                myId={me?.id ?? null}
                myRole={me?.role ?? null}
                onEdit={() => setEditingId(user.id)}
              />
            ))}
          </ListGroup>

          <DataTable
            minWidth="56rem"
            columns={[
              { key: 'employeeNo', label: t('user.employeeNo'), className: 'w-24' },
              { key: 'name', label: t('user.name') },
              // 归哪家只对平台账号有意义（CR-073）：单位管理员看到的全是自己家的人
              ...(isPlatform ? [{ key: 'org', label: t('org.column'), className: 'w-32' }] : []),
              { key: 'role', label: t('user.role'), className: 'w-40' },
              { key: 'rate', label: t('user.hourlyRate'), align: 'right' as const, className: 'w-28' },
              { key: 'status', label: t('user.status'), className: 'w-28' },
              { key: 'login', label: t('user.lastLogin'), className: 'w-40' },
            ]}
          >
            {data.items.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                myId={me?.id ?? null}
                myRole={me?.role ?? null}
                selected={editingId === user.id}
                onEdit={() => setEditingId(user.id)}
              />
            ))}
          </DataTable>
        </>
      )}

      {data && data.total > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <label className="text-caption text-ink-3" htmlFor="u-page-size">
              {t('user.perPage')}
            </label>
            <Select
              id="u-page-size"
              className="w-20"
              value={String(pageSize)}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
            <span className="text-caption text-ink-3">
              {t('user.pageSummary', {
                from: (data.page - 1) * data.pageSize + 1,
                to: Math.min(data.page * data.pageSize, data.total),
                total: data.total,
              })}
            </span>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" disabled={data.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                {t('app.prev')}
              </Button>
              <span className="min-w-14 text-center text-caption tabular-nums text-ink-3">
                {data.page} / {totalPages}
              </span>
              <Button variant="secondary" size="sm" disabled={data.page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                {t('app.next')}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/**
 * 能不能打开这一行的「编辑」：得有 user.manage，而且对方的职位是自己管得了的（canManageRole，CR-038）；
 * 超管自己那一行也能开（没人管得了超管，只有本人能改自己的密码）。服务端也会挡，界面上就不该出现点了才报错的入口。
 */
function useCanEdit(user: User, myId: number | null, myRole: RoleSummary | null): boolean {
  const { can } = usePermissions();
  const canManage = can(Permission.USER_MANAGE) && myRole !== null && canManageRole(myRole, user.role);
  const isSelfSystem = user.id === myId && user.role.isSystem;
  return canManage || isSelfSystem;
}

/**
 * 表格的一行（CR-082）：**点一行打开编辑**（CR-078 的资料 / 密码 / 账号状态在右侧面板的页签里）。
 * 改之前行尾一个「编辑」按钮；更早是一行四个按钮，表格被撑得很宽。管不了的那一行点不开。
 */
function UserRow({
  user,
  myId,
  myRole,
  selected,
  onEdit,
}: {
  user: User;
  myId: number | null;
  myRole: RoleSummary | null;
  selected: boolean;
  onEdit: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { isPlatform } = usePermissions();
  const canEdit = useCanEdit(user, myId, myRole);
  const cells = (
    <>
      <td className="whitespace-nowrap text-caption tabular-nums text-ink-3">{user.employeeNo}</td>
      <td className="truncate font-medium text-ink">{user.name}</td>
      {isPlatform && <td className="truncate text-ink-2">{user.org?.name ?? t('org.platform')}</td>}
      <td className="whitespace-nowrap text-ink-2">
        {user.role.name}
        {user.role.isSystem && (
          <span className="ml-1.5">
            <Tag tone="neutral">{t('roles.system')}</Tag>
          </span>
        )}
      </td>
      <td className="whitespace-nowrap text-right tabular-nums text-ink-2">
        {user.hourlyRateCents == null ? '—' : formatMoney(user.hourlyRateCents, i18n.language)}
      </td>
      <td className="whitespace-nowrap">
        <Badge tone={user.status === UserStatus.ACTIVE ? 'ok' : 'neutral'}>
          {user.status === UserStatus.ACTIVE ? t('user.statusActive') : t('user.statusSuspended')}
        </Badge>
      </td>
      <td className="whitespace-nowrap text-caption tabular-nums text-ink-3">
        {user.lastLoginAt
          ? new Date(user.lastLoginAt).toLocaleString(i18n.language, { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
          : t('user.neverLoggedIn')}
      </td>
    </>
  );
  return canEdit ? (
    <TableRow selected={selected} onOpen={onEdit}>
      {cells}
    </TableRow>
  ) : (
    <tr>{cells}</tr>
  );
}

/** 窄屏、App 的一行：名字（停用的带标签），工号 · 职位 · 公司；右边最近登录 */
function UserListRow({
  user,
  myId,
  myRole,
  onEdit,
}: {
  user: User;
  myId: number | null;
  myRole: RoleSummary | null;
  onEdit: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { isPlatform } = usePermissions();
  const canEdit = useCanEdit(user, myId, myRole);
  return (
    <ListRow
      onClick={canEdit ? onEdit : undefined}
      title={
        <span className="inline-flex items-center gap-2">
          {user.name}
          {user.status !== UserStatus.ACTIVE && <Tag tone="neutral">{t('user.statusSuspended')}</Tag>}
        </span>
      }
      subtitle={[`${t('user.employeeNo')} ${user.employeeNo}`, user.role.name, isPlatform ? (user.org?.name ?? t('org.platform')) : null]
        .filter(Boolean)
        .join(' · ')}
      trailing={
        <span className="tabular-nums">
          {user.lastLoginAt
            ? new Date(user.lastLoginAt).toLocaleDateString(i18n.language, { month: 'numeric', day: 'numeric' })
            : t('user.neverLoggedIn')}
        </span>
      }
    />
  );
}

type EditTab = 'profile' | 'password' | 'status';

/**
 * 「编辑」抽屉里的内容（CR-078）：资料 / 密码 / 账号状态三个页签，**只出现这个人能用的**：
 * 管得了他（canManageRole）三个都有；超管自己那一行只有「密码」（没人管得了超管，只有本人能改自己的密码）。
 */
function UserEditPanel({
  user,
  myId,
  myRole,
  roles,
  onClose,
}: {
  user: User;
  myId: number | null;
  myRole: RoleSummary;
  roles: Role[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canManage = can(Permission.USER_MANAGE) && canManageRole(myRole, user.role);
  const isSelfSystem = user.id === myId && user.role.isSystem;

  const tabs: Array<{ value: EditTab; label: string }> = [
    ...(canManage ? [{ value: 'profile' as const, label: t('user.tabProfile') }] : []),
    ...(canManage || isSelfSystem ? [{ value: 'password' as const, label: t('user.tabPassword') }] : []),
    ...(canManage ? [{ value: 'status' as const, label: t('user.tabStatus') }] : []),
  ];
  const [tab, setTab] = useState<EditTab>(tabs[0]?.value ?? 'password');

  return (
    <div className="p-5">
      {tabs.length > 1 && (
        <StatusTabs
          tabs={tabs}
          value={tab}
          onChange={(next) => next && setTab(next)}
          label={t('user.editSections')}
        />
      )}
      <div className="mt-2">
        {tab === 'profile' && <EditUserForm user={user} myRole={myRole} roles={roles} onDone={onClose} />}
        {tab === 'password' && <PasswordPanel user={user} isSelf={user.id === myId} />}
        {tab === 'status' && <StatusPanel user={user} />}
      </div>
    </div>
  );
}

/**
 * 「密码」页签：设置指定密码（CR-077）在上，随机重置在下。
 * 两种都能改掉他的密码，区别写在各自下面：设置 = 管理员定、默认不要求再改；重置 = 系统随机、只显示一次、登录后必须改。
 */
function PasswordPanel({ user, isSelf }: { user: User; isSelf: boolean }) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const [done, setDone] = useState(false);
  // 设好之后清空表单：换 key 让它重新挂载
  const [formKey, setFormKey] = useState(0);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isSelfSystem = isSelf && user.role.isSystem;

  const reset = useMutation({
    mutationFn: () => usersApi.resetPassword(user.id),
    onSuccess: (data) => setRevealed(data.initialPassword),
    onError: (err) => setError(toMessage(err)),
  });

  const confirmReset = () => {
    setError(null);
    const message = isSelfSystem
      ? t('user.resetOwnPasswordConfirm')
      : t('user.resetPasswordConfirm', { name: user.name });
    if (window.confirm(message)) reset.mutate();
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <h3 className="text-title font-semibold text-ink">{t('user.setPassword')}</h3>
        {done && <Alert tone="info">{t('user.passwordSet', { name: user.name })}</Alert>}
        <SetPasswordForm
          key={formKey}
          user={user}
          isSelf={isSelf}
          onDone={() => {
            setDone(true);
            setFormKey((k) => k + 1);
          }}
        />
      </section>

      <section className="flex flex-col gap-3 border-t border-rule pt-5">
        <h3 className="text-title font-semibold text-ink">{t('user.resetPassword')}</h3>
        <p className="text-body text-ink-3">{t('user.resetPasswordHint')}</p>
        {error && <Alert>{error}</Alert>}
        {revealed ? (
          <PasswordReveal
            password={revealed}
            // 给自己重置的不用交给别人，也不用再改一次（服务端没设 mustChangePassword）
            hintKey={isSelfSystem ? 'user.ownPasswordHint' : 'user.initialPasswordHint'}
            onDismiss={() => setRevealed(null)}
          />
        ) : (
          <div>
            <Button variant="secondary" onClick={confirmReset} loading={reset.isPending}>
              {t('user.resetPassword')}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}

/** 「账号状态」页签：停用 / 启用。停用会立刻把他踢下线（服务端吊销会话），记录都保留 */
function StatusPanel({ user }: { user: User }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const [error, setError] = useState<string | null>(null);
  const active = user.status === UserStatus.ACTIVE;

  const toggle = useMutation({
    mutationFn: () =>
      usersApi.update(user.id, { status: active ? UserStatus.SUSPENDED : UserStatus.ACTIVE }),
    // 抽屉里的 user 取自列表，列表一刷新这里就显示新的状态
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="flex items-center gap-2 text-body text-ink-2">
        {t('user.status')}
        <Badge tone={active ? 'ok' : 'danger'}>
          {active ? t('user.statusActive') : t('user.statusSuspended')}
        </Badge>
      </p>
      <p className="text-body leading-relaxed text-ink-2">
        {active ? t('user.statusHintActive') : t('user.statusHintSuspended')}
      </p>
      {error && <Alert>{error}</Alert>}
      <div>
        <Button
          variant={active ? 'danger' : 'primary'}
          onClick={() => {
            setError(null);
            toggle.mutate();
          }}
          loading={toggle.isPending}
        >
          {active ? t('user.suspend') : t('user.activate')}
        </Button>
      </div>
    </div>
  );
}

/**
 * 直接设一个指定的密码（CR-077，客户原话「用户管理那里增加修改密码，直接写入指定密码」）。
 *
 * 和「重置密码」的区别：重置是系统随机生成、只显示一次、对方登录后必须改；这里是管理员自己定的，
 * 默认**不**要求对方再改（客户要的就是给工人设一个好记的），要求的话勾上。长度规则和自己改密码同一份。
 * 给别人设会把他所有设备踢下线；给自己设不踢。密码框 `autoComplete="off"`（约束 8：手机多人轮换，不让 WebView 记住）。
 */
function SetPasswordForm({
  user,
  isSelf,
  onDone,
}: {
  user: User;
  isSelf: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mustChange, setMustChange] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => usersApi.setPassword(user.id, { password, ...(isSelf ? {} : { mustChange }) }),
    onSuccess: () => onDone(),
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          if (password.length < PASSWORD_MIN_LENGTH) {
            setError(t('auth.passwordTooShort'));
            return;
          }
          if (password !== confirm) {
            setError(t('auth.passwordMismatch'));
            return;
          }
          save.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <p className="text-body text-ink-2">
          {isSelf ? t('user.setPasswordSelfHint') : t('user.setPasswordHint', { name: user.name })}
        </p>
        {error && <Alert>{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('auth.newPassword')} htmlFor={`pw-new-${user.id}`} required>
            <Input
              id={`pw-new-${user.id}`}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
              minLength={PASSWORD_MIN_LENGTH}
              required
            />
          </Field>
          <Field label={t('auth.confirmPassword')} htmlFor={`pw-confirm-${user.id}`} required>
            <Input
              id={`pw-confirm-${user.id}`}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="off"
              required
            />
          </Field>
        </div>
        {!isSelf && (
          <Checkbox
            label={t('user.mustChangeNext')}
            checked={mustChange}
            onChange={(e) => setMustChange(e.target.checked)}
          />
        )}
        <div>
          <Button type="submit" loading={save.isPending}>
            {t('user.setPassword')}
          </Button>
        </div>
      </form>
  );
}

/**
 * 一次性密码展示。刻意做得显眼并且需要手动关闭 ——
 * 这串字符关掉就再也看不到了，管理员必须当场抄下来交给工人。
 */
interface CreatedAccount {
  user: User;
  password: string;
}

/**
 * 新账号凭单（CR-017）。
 *
 * 临时密码**只在这一次响应里出现**，数据库里只有哈希。所以这里一并给出
 * 打印入口，管理员当场导出一张交给本人 —— 否则只能对着屏幕抄工号和密码，
 * 抄错一位工人就登不进去。
 *
 * 用浏览器的「打印 → 另存为 PDF」而不是前端生成 PDF：中文 PDF 要嵌中文字体，
 * 打包进来好几兆，而系统打印对话框用的是系统字体，中文天然没问题。
 * 代价是安卓 App 里可能调不起打印 —— 建账号本来就是坐在电脑前做的事。
 */
function AccountHandout({
  account,
  onDismiss,
}: {
  account: CreatedAccount;
  onDismiss: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { user, password } = account;

  return (
    <>
      {/* 屏幕上看到的 */}
      <div className="rounded-md border-2 border-safety-500 bg-safety-50 p-4 print:hidden">
        <p className="text-caption font-medium uppercase tracking-wide text-safety-600">
          {t('user.accountCreated')}
        </p>

        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <Pair label={t('user.employeeNo')} value={user.employeeNo} mono />
          <Pair label={t('user.name')} value={user.name} />
          <Pair label={t('user.role')} value={user.role.name} />
        </dl>

        <p className="mt-3 text-caption font-medium uppercase tracking-wide text-safety-600">
          {t('user.initialPassword')}
        </p>
        <p className="my-1 select-all font-mono text-figure font-semibold tracking-wider text-ink">
          {password}
        </p>
        <p className="mb-3 text-body leading-relaxed text-ink-2">{t('user.initialPasswordHint')}</p>

        <div className="flex gap-3">
          <Button onClick={() => window.print()}>{t('user.exportPdf')}</Button>
          <Button variant="secondary" onClick={onDismiss}>
            {t('app.confirm')}
          </Button>
        </div>
      </div>

      {/* 打印出来的那张。
          **必须 portal 到 body 下面**：它渲染在抽屉里，而抽屉是 print:hidden，
          父级 display:none 之后子元素怎么设都出不来 —— 打印会是一张白纸。
          挂到 body 下还顺带绕开了抽屉的 fixed 定位和 overflow 裁剪。 */}
      {createPortal(
        <div className="print-handout hidden print:block">
          <h1 className="text-heading font-semibold">{t('user.handoutTitle')}</h1>
          <p className="mt-1 text-body text-ink-2">{t('app.name')}</p>

          <dl className="mt-6 grid gap-y-3">
            <Pair label={t('user.employeeNo')} value={user.employeeNo} mono big />
            <Pair label={t('user.initialPassword')} value={password} mono big />
            <Pair label={t('user.name')} value={user.name} />
            <Pair label={t('user.role')} value={user.role.name} />
            <Pair label={t('user.handoutLoginAt')} value={window.location.origin} mono />
          </dl>

          <p className="mt-6 text-body leading-relaxed">{t('user.handoutFirstLogin')}</p>
          <p className="mt-2 text-body leading-relaxed">{t('user.handoutKeepSafe')}</p>

          <p className="mt-8 text-caption text-ink-3">
            {t('user.handoutIssuedAt', {
              time: new Date().toLocaleString(i18n.language),
            })}
          </p>
        </div>,
        document.body,
      )}
    </>
  );
}

function Pair({
  label,
  value,
  mono,
  big,
}: {
  label: string;
  value: string;
  mono?: boolean;
  big?: boolean;
}) {
  return (
    <div>
      <dt className="text-caption text-ink-3">{label}</dt>
      <dd
        className={[
          'mt-0.5',
          mono ? 'font-mono tracking-wider' : '',
          big ? 'text-figure font-semibold' : 'text-title',
        ].join(' ')}
      >
        {value}
      </dd>
    </div>
  );
}

/** 重置密码后只有新密码，没有完整账号信息，所以单独留一个简版 */
function PasswordReveal({
  password,
  hintKey,
  onDismiss,
}: {
  password: string;
  hintKey: 'user.initialPasswordHint' | 'user.ownPasswordHint';
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border-2 border-safety-500 bg-safety-50 p-4">
      <p className="text-caption font-medium uppercase tracking-wide text-safety-600">
        {t('user.initialPassword')}
      </p>
      <p className="my-2 select-all font-mono text-figure font-semibold tracking-wider text-ink">
        {password}
      </p>
      <p className="mb-3 text-body leading-relaxed text-ink-2">
        {hintKey === 'user.ownPasswordHint'
          ? t('user.ownPasswordHint')
          : t('user.initialPasswordHint')}
      </p>
      <Button variant="secondary" onClick={onDismiss}>
        {t('app.confirm')}
      </Button>
    </div>
  );
}

/**
 * 角色下拉 + 下面一句这个角色能做什么（CR-027 / CR-038）。
 *
 * 只列自己管得了的角色（assignableRoles）：超管看到所有非系统角色，
 * 账号管理员只看到「权限是自己子集、且不带 user.manage」的。
 * 超级管理员那条系统角色任何人都设不出来。
 * 提示是角色的 `description` —— 角色是客户建的数据，说明也由他写。
 */
function RoleSelect({
  id,
  value,
  onChange,
  options,
  current,
}: {
  id: string;
  value: number | '';
  onChange: (roleId: number) => void;
  options: Role[];
  /** 编辑时这个人当前的角色，可能不在可设列表里（比如超管看另一个超管） */
  current?: RoleSummary;
}) {
  const { t } = useTranslation();
  const selected = options.find((r) => r.id === value);
  const hint = selected?.description || (options.length === 0 ? t('user.noAssignableRole') : undefined);

  return (
    <Field label={t('user.role')} htmlFor={id} required hint={hint}>
      <Select
        id={id}
        value={value === '' ? '' : String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={options.length === 0 && !current}
      >
        {value === '' && <option value="">{t('user.pickRole')}</option>}
        {/* 当前值不在可设列表里也列出来，否则下拉会显示成别的角色 */}
        {current && !options.some((r) => r.id === current.id) && (
          <option value={current.id}>{current.name}</option>
        )}
        {options.map((role) => (
          <option key={role.id} value={role.id}>
            {role.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function CreateUserForm({ roles, onDone }: { roles: Role[]; onDone: () => void }) {
  const { t } = useTranslation();
  const myRole = useAuthStore((s) => s.user?.role ?? null);
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const { can, isSystem, isPlatform, orgId: myOrgId } = usePermissions();

  // 归哪家（CR-073）：单位管理员建的永远是自己家的（表单里没有这一项）；
  // 平台账号必须选，默认不选 —— 默认值会让人不看就点保存，把人建到错的那家
  const [orgChoice, setOrgChoice] = useState<OrgChoice>('');
  const target = isPlatform ? (orgChoice === '' ? undefined : orgChoice) : myOrgId;
  const options = myRole ? rolesFor(myRole, roles, target) : [];

  const [form, setForm] = useState<{
    name: string;
    phone: string;
    roleId: number | '';
    hourlyRate: string;
  }>({
    name: '',
    phone: '',
    roleId: '',
    hourlyRate: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedAccount | null>(null);

  // 建账号时顺手勾工地（CR-071）：不用建完再去工地设置里一个个加。门槛和工地设置里加人一样。
  // 只列这个人进得去的（CR-073）：同一家的；平台账号哪家都进得去；还没选归哪家就先不列
  const canSites = can(Permission.SITE_MANAGE);
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => sitesApi.list(), enabled: canSites });
  const [siteIds, setSiteIds] = useState<number[]>([]);
  const siteItems =
    target === undefined ? [] : (sites.data ?? []).filter((s) => userFitsSite(target, s.orgId));

  /** 换了归哪家：挂不上的角色、进不去的工地一并清掉，别让人带着上一家的选择保存 */
  const changeOrg = (next: OrgChoice) => {
    setOrgChoice(next);
    const nextTarget = next === '' ? undefined : next;
    const fits = myRole ? rolesFor(myRole, roles, nextTarget) : [];
    setForm((f) => (f.roleId !== '' && !fits.some((r) => r.id === f.roleId) ? { ...f, roleId: '' } : f));
    setSiteIds([]);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const data = await usersApi.create({
        // 工号由服务端发：它就是新账号的 id（CR-020），接口不收这个字段
        name: form.name.trim(),
        phone: form.phone.trim() || undefined,
        roleId: form.roleId as number,
        ...(isPlatform ? { orgId: orgChoice as number | null } : {}),
        // 界面填的是元，存的是分
        hourlyRateCents: form.hourlyRate ? Math.round(Number(form.hourlyRate) * 100) : undefined,
      });
      // 账号已经建好了，加工地失败不能让人以为没建成、更不能吞掉初始密码：
      // 错误另外显示，密码照常交出去，工地到用户页编辑里再补
      if (canSites && siteIds.length > 0) {
        try {
          await usersApi.setSites(data.user.id, siteIds);
          void queryClient.invalidateQueries({ queryKey: ['site-members'] });
        } catch (err) {
          setError(toMessage(err));
        }
      }
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      setCreated({ user: data.user, password: data.initialPassword });
    },
    onError: (err) => setError(toMessage(err)),
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (isPlatform && orgChoice === '') {
      setError(t('errors.ORG_REQUIRED'));
      return;
    }
    if (form.roleId === '') {
      setError(t('user.pickRole'));
      return;
    }
    mutation.mutate();
  }

  if (created) {
    return (
      <Card className="mb-6 p-5">
        <AccountHandout
          account={created}
          onDismiss={() => {
            setCreated(null);
            onDone();
          }}
        />
      </Card>
    );
  }

  const set = (key: 'name' | 'phone' | 'hourlyRate') => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Card className="mb-6 p-5">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert>{error}</Alert>}

        {isPlatform && (
          <OrgSelect
            id="u-org-pick"
            label={t('user.org')}
            value={orgChoice}
            onChange={changeOrg}
            // 平台账号只有超管能建（CR-073）
            platformOption={isSystem ? t('user.orgPlatformOption') : undefined}
            hint={t('user.orgHint')}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('user.name')} htmlFor="u-name" required>
            <Input id="u-name" value={form.name} onChange={set('name')} required />
          </Field>
          <Field label={t('user.phone')} htmlFor="u-phone">
            <Input id="u-phone" value={form.phone} onChange={set('phone')} inputMode="tel" />
          </Field>
          <RoleSelect
            id="u-role"
            value={form.roleId}
            onChange={(roleId) => setForm((f) => ({ ...f, roleId }))}
            options={options}
          />
          <Field label={t('user.hourlyRate')} htmlFor="u-rate">
            <Input
              id="u-rate"
              value={form.hourlyRate}
              onChange={set('hourlyRate')}
              inputMode="decimal"
              placeholder="25.00"
            />
          </Field>
        </div>

        {canSites && siteItems.length > 0 && (
          <div>
            <p className="mb-1 text-body font-medium text-ink-2">{t('user.sites')}</p>
            <p className="mb-2 text-caption leading-relaxed text-ink-3">{t('user.sitesCreateHint')}</p>
            <CheckList
              items={siteItems.map((s) => ({ id: s.id, label: s.name }))}
              selected={siteIds}
              onChange={setSiteIds}
              labels={{
                search: t('site.searchSites'),
                selectAll: t('site.selectAll'),
                clearAll: t('site.clearAll'),
                empty: t('site.noMatches'),
                picked: t('site.pickedCount', { count: siteIds.length }),
              }}
            />
          </div>
        )}

        <div className="flex gap-3">
          <Button type="submit" loading={mutation.isPending}>
            {t('app.save')}
          </Button>
          <Button type="button" variant="secondary" onClick={onDone}>
            {t('app.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * 改用户资料。
 *
 * 能改什么受 canManageRole 限制：角色下拉里只列自己管得了的，而这一行本身只有在
 * 「我管得了他」时才会出现编辑按钮。服务端会再校验一遍 ——
 * 界面上的限制是给人看的，不是安全边界。
 */
function EditUserForm({
  user,
  myRole,
  roles,
  onDone,
}: {
  user: User;
  myRole: RoleSummary;
  roles: Role[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const { can, isSystem, isPlatform } = usePermissions();
  const [error, setError] = useState<string | null>(null);

  // 挪到别家（CR-073）：只有平台账号能挪。挪完他原来那家的工地全部移出、要重新登录（服务端做）
  const [orgChoice, setOrgChoice] = useState<OrgChoice>(user.orgId);
  const target = orgChoice === '' ? user.orgId : orgChoice;
  const orgChanged = target !== user.orgId;

  const [form, setForm] = useState({
    name: user.name,
    phone: user.phone ?? '',
    roleId: user.roleId as number | '',
    hourlyRate: user.hourlyRateCents == null ? '' : String(user.hourlyRateCents / 100),
  });

  // 所在工地（CR-071）：动的是成员关系，门槛和工地设置里加人一样（site.manage）。
  // 勾选框只列操作者看得见的在建工地，服务端也只在这个范围内比差异
  const canSites = can(Permission.SITE_MANAGE);
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => sitesApi.list(), enabled: canSites });
  const currentSites = useQuery({
    queryKey: ['user-sites', user.id],
    queryFn: () => usersApi.sites(user.id),
    enabled: canSites,
  });
  // null = 还没动过，保存时不提交；避免回显还没加载完就把人从所有工地移出去
  const [siteIds, setSiteIds] = useState<number[] | null>(null);
  const shownSiteIds = siteIds ?? currentSites.data ?? [];
  // 只列这个人进得去的工地（CR-073）；挪了家就按新的那家列
  const siteItems = (sites.data ?? []).filter((s) => userFitsSite(target, s.orgId));

  const options = rolesFor(myRole, roles, target);
  const roleChanged = form.roleId !== '' && form.roleId !== user.roleId;
  // 挪了家但原来的角色挂不上新的那家（上一家自建的、或管理员挪成平台账号）：必须换一个
  const roleStale = orgChanged && !roleChanged && !canHoldRole(user.role, target);

  const changeOrg = (next: OrgChoice) => {
    setOrgChoice(next);
    // 原来那家的工地保存后会被全部移出，勾选从空的开始
    setSiteIds(next === user.orgId ? null : []);
  };

  const save = useMutation({
    mutationFn: async () => {
      const updated = await usersApi.update(user.id, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        // 角色没动就不提交，免得触发一次没必要的会话作废
        ...(roleChanged ? { roleId: form.roleId as number } : {}),
        ...(orgChanged ? { orgId: target } : {}),
        hourlyRateCents:
          form.hourlyRate.trim() === '' ? undefined : Math.round(Number(form.hourlyRate) * 100),
      });
      if (canSites && siteIds !== null) await usersApi.setSites(user.id, siteIds);
      return updated;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['user-sites', user.id] });
      void queryClient.invalidateQueries({ queryKey: ['site-members'] });
      onDone();
    },
    onError: (err) => {
      setError(toMessage(err));
      // 保存是两步（改资料 → 勾工地），第一步可能已经存上了：列表跟着刷，别让页面停在旧的职位 / 公司上
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const set = (key: 'name' | 'phone' | 'hourlyRate') => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          if (roleStale) {
            setError(t('errors.ORG_MISMATCH'));
            return;
          }
          save.mutate();
        }}
        className="flex flex-col gap-4"
      >
        {error && <Alert>{error}</Alert>}

        {isPlatform && (
          <OrgSelect
            id={`e-org-${user.id}`}
            label={t('user.org')}
            value={orgChoice}
            onChange={changeOrg}
            platformOption={isSystem || user.orgId === null ? t('user.orgPlatformOption') : undefined}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('user.name')} htmlFor={`e-name-${user.id}`} required>
            <Input
              id={`e-name-${user.id}`}
              value={form.name}
              onChange={set('name')}
              required
              maxLength={64}
            />
          </Field>

          <Field label={t('user.phone')} htmlFor={`e-phone-${user.id}`}>
            <Input
              id={`e-phone-${user.id}`}
              value={form.phone}
              onChange={set('phone')}
              maxLength={32}
            />
          </Field>

          <RoleSelect
            id={`e-role-${user.id}`}
            value={form.roleId}
            onChange={(roleId) => setForm((f) => ({ ...f, roleId }))}
            options={options}
            current={user.role}
          />

          <Field label={t('user.hourlyRate')} htmlFor={`e-rate-${user.id}`}>
            <Input
              id={`e-rate-${user.id}`}
              value={form.hourlyRate}
              onChange={set('hourlyRate')}
              inputMode="decimal"
              placeholder="25.00"
            />
          </Field>
        </div>

        {roleChanged && <Alert tone="info">{t('user.roleChangeHint')}</Alert>}
        {orgChanged && <Alert tone="info">{t('user.orgMoveHint')}</Alert>}
        {roleStale && <Alert>{t('user.orgMoveRoleHint')}</Alert>}

        {canSites && (
          <div>
            <p className="mb-1 text-body font-medium text-ink-2">{t('user.sites')}</p>
            <p className="mb-2 text-caption leading-relaxed text-ink-3">{t('user.sitesHint')}</p>
            {currentSites.isLoading || sites.isLoading ? (
              <p className="text-body text-ink-3">{t('app.loading')}</p>
            ) : (
              <CheckList
                items={siteItems.map((s) => ({ id: s.id, label: s.name }))}
                selected={shownSiteIds}
                onChange={setSiteIds}
                labels={{
                  search: t('site.searchSites'),
                  selectAll: t('site.selectAll'),
                  clearAll: t('site.clearAll'),
                  empty: t('site.noMatches'),
                  picked: t('site.pickedCount', { count: shownSiteIds.length }),
                }}
              />
            )}
          </div>
        )}

        <div className="flex gap-3">
          <Button type="submit" loading={save.isPending}>
            {t('app.save')}
          </Button>
          <Button type="button" variant="secondary" onClick={onDone}>
            {t('app.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
